import React, { useState, useEffect, useRef } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import Marquee from "react-fast-marquee";
import HitCounter from "../ui/HitCounter";
import ResourceNavigator from "../resources/ResourceNavigator";
import GrantAwardees from "../awardees/GrantAwardees";
import DonateContent from "../donate/DonateContent";
import OSIcon32 from "../icons/OSIcon32";
import { getCurrentQuarter } from "../../services/statsTracking";
import { useLandingImpactStats } from "../../helpers/impactStats";

import {
  SEO_H1,
  HOME_HERO,
  MISSION,
  IMPACT,
  HOW_IT_WORKS,
  HOW_IT_WORKS_STEPS,
  PRESS,
  PRESS_ITEMS,
  AWARDEES_MARQUEE_HEAD,
  AWARDEES_MARQUEE,
  TESTIMONIAL,
  FINAL_CTA,
  FOOTER_LINKS,
  COPYRIGHT_NOTICE,
  LEGACY_CHAPTER_CARDS,
  CHAPTERS_HERO,
  CHAPTER_CARD_CTAS,
  HOW_CHAPTERS_WORK,
  COLLECTIVE_GIVING,
  START_A_CHAPTER,
  AWARDEES_PAGE,
} from "../../data/landingContent";

// End-of-quarter application deadline, derived from the live quarter so the
// ticker and the testimonial panel never go stale. "Q3 2026" → "Sep 30 · 11:59 pm".
const quarterDeadline = () => {
  const [q, year] = getCurrentQuarter().split(" ");
  const endMonth = parseInt(q.substring(1), 10) * 3; // 1-indexed month after the quarter
  const lastDay = new Date(parseInt(year, 10), endMonth, 0);
  const label = lastDay.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${label} · 11:59 pm`;
};

const CHAPTER_NAMES = "Western New York · Denver · Central NY · Capital Region · Hudson Valley";

export default function BrowserWindow({ onClose, onPitchClick, onLpApplicationClick, windowId, zIndex, bringToFront, pageRequest }) {
  const [history, setHistory] = useState(["home"]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [currentMarqueeIndex, setCurrentMarqueeIndex] = useState(0);
  const scrollContainerRef = useRef(null);

  const currentPage = history[historyIndex];

  // Imperative navigation from the desktop (icons, tray shortcuts). The
  // request carries a nonce so repeating the same page still navigates.
  const lastPageRequestRef = useRef(null);
  useEffect(() => {
    if (!pageRequest || pageRequest.nonce === lastPageRequestRef.current) return;
    lastPageRequestRef.current = pageRequest.nonce;
    setHistory((prev) => {
      const next = [...prev.slice(0, historyIndex + 1), pageRequest.page];
      setHistoryIndex(next.length - 1);
      return next;
    });
  }, [pageRequest, historyIndex]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [currentPage]);
  const marqueeImages = [];


  // Advance the marquee every 5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentMarqueeIndex((prevIndex) =>
        marqueeImages.length > 0
          ? (prevIndex + 1) % marqueeImages.length
          : 0
      );
    }, 5000);
    
    return () => clearInterval(timer);
  }, []);

  const setPage = (page) => {
    const newHistory = [...history.slice(0, historyIndex + 1), page];
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const goBack = () => {
    if (historyIndex > 0) setHistoryIndex(historyIndex - 1);
  };

  const goForward = () => {
    if (historyIndex < history.length - 1) setHistoryIndex(historyIndex + 1);
  };

  const handleWindowClick = () => {
    if (windowId && bringToFront) {
      bringToFront(windowId);
    }
  };

  const renderPage = () => {
    switch (currentPage) {
      case "home":
        return <HomePage onPitchClick={onPitchClick} />;
      case "chapters":
        return <ChaptersPage setPage={setPage} onLpApplicationClick={onLpApplicationClick} onPitchClick={onPitchClick} />;
      case "awardees":
        return <AwardeesPage onPitchClick={onPitchClick} />;
      case "resources":
        return <ResourceNavigator isEmbedded={true} />;
      case "donate":
        return <DonatePage onPitchClick={onPitchClick} />;
      default:
        return <HomePage onPitchClick={onPitchClick} />;
    }
  };

  return (
    <div 
      style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
      onClick={handleWindowClick}
    >
      {/* Browser chrome — single ink bar: nav buttons + URL */}
      <div className="mb-browser-bar">
        <div className="mb-browser-nav">
          <button
            className="mb-nav-btn"
            onClick={goBack}
            disabled={historyIndex === 0}
            aria-label="Go back"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <polygon points="10,2 10,14 3,8" fill="currentColor" />
              <rect x="10" y="7" width="4" height="2" fill="currentColor" />
            </svg>
          </button>
          <button
            className="mb-nav-btn"
            onClick={goForward}
            disabled={historyIndex === history.length - 1}
            aria-label="Go forward"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <polygon points="6,2 6,14 13,8" fill="currentColor" />
              <rect x="2" y="7" width="4" height="2" fill="currentColor" />
            </svg>
          </button>
          <button
            className="mb-nav-btn"
            onClick={() => window.location.reload()}
            aria-label="Refresh page"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <path d="M 8 3 A 5 5 0 1 1 3.5 10.5" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="square" />
              <polygon points="7,1 11,3.5 7,6" fill="currentColor" />
            </svg>
          </button>
          <button
            className="mb-nav-btn"
            onClick={() => setPage("home")}
            aria-label="Go to home page"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <polygon points="8,1 15,7 13,7 13,14 9,14 9,10 7,10 7,14 3,14 3,7 1,7" fill="currentColor" />
            </svg>
          </button>
        </div>

        <span className="mb-browser-addr" style={{
          fontFamily: "var(--font-chrome)",
          fontSize: 11,
          fontWeight: 600,
          color: "var(--chrome-outline)",
          marginLeft: 4
        }}>Address</span>
        <div className="mb-browser-url" style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, padding: "0 4px", minWidth: 0 }}>
          <span className="mb-browser-url-tag" style={{
            background: "var(--mb-aqua)",
            color: "var(--mb-chalk)",
            padding: "1px 5px",
            flex: "none"
          }}>https</span>
          <span className="mb-browser-url-path" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            www.goodneighbor.fund{currentPage === "home" ? "/" : `/${currentPage}`}
          </span>
        </div>
        <button
          type="button"
          className="win95-btn win95-btn-primary win95-btn--compact"
          style={{ flex: "none" }}
          onClick={onPitchClick}
        >
          Apply
        </button>
      </div>

      {/* Folder-tab nav */}
      <nav role="navigation" aria-label="Main navigation" className="mb-tabs">
        {[
          { id: "home", label: "Home", icon: "navigator", matches: (p) => p === "home" },
          { id: "chapters", label: "Chapters", icon: "chapters", matches: (p) => p === "chapters" || p === "wny" || p === "denver" },
          { id: "awardees", label: "Awardees", icon: "trophy", matches: (p) => p === "awardees" },
          { id: "resources", label: "Resources", icon: "resources", matches: (p) => p === "resources" },
          { id: "donate", label: "Donate", icon: "donate", matches: (p) => p === "donate" }
        ].map((tab) => {
          const isActive = tab.matches(currentPage);
          return (
            <button
              key={tab.id}
              onClick={() => setPage(tab.id)}
              className={`mb-tab ${isActive ? "is-active" : ""}`}
              aria-current={isActive ? "page" : undefined}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <OSIcon32 kind={tab.icon} size={15} />
              {tab.label}
            </button>
          );
        })}
      </nav>
      {/* Main Content — sunken well; each page composes its own .mb-block sections */}
      <div ref={scrollContainerRef} className="win95-scroll" style={{
        overflowY: currentPage === "resources" ? "hidden" : "auto",
        padding: 0,
        background: "var(--mb-paper)",
        minHeight: 0,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        border: "2px solid",
        borderColor: "var(--bevel-sunken)",
        borderBottom: "none",
        margin: "0 2px"
      }}>
        {renderPage()}
      </div>

      {/* Status bar */}
      <div className="win95-statusbar">
        <div className="win95-statusbar__pane win95-statusbar__pane--grow">Done</div>
        <div className="win95-statusbar__pane win95-statusbar__pane--data">
          {getCurrentQuarter()} applications open
        </div>
        <div className="win95-statusbar__pane">Internet zone</div>
      </div>
    </div>
  );
}


// -- HomePage --
// The calmer four-block landing: ticker, hero, group-box band, testimonial.
// The slim ink footer (hit counter, social, terms/privacy) follows so the
// landing keeps its legal links; the big duplicate CTA block lives on the
// other tabs via PageCTAFooter.
const HomePage = ({ onPitchClick }) => {
  // Live all-chapter totals from publicWinners; hardcoded copy until they load.
  // The ticker and hero rail repeat the funded count, so they read it here too.
  const impactStats = useLandingImpactStats();
  const fundedCount = impactStats[0].n;
  return (
  <main className="mb-content">
    <h1 style={{ position: "absolute", width: "1px", height: "1px", padding: 0, margin: "-1px", overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}>{SEO_H1}</h1>

    {/* ===== TICKER — ink bar with a fixed chip and a scrolling track ===== */}
    <div style={{
      background: "var(--mb-ink)",
      color: "var(--mb-chalk)",
      minHeight: "34px",
      display: "flex",
      alignItems: "center",
      borderBottom: "var(--border-ink-2)",
      fontFamily: "var(--font-numeral)",
      fontSize: "11px",
      whiteSpace: "nowrap",
      overflow: "hidden"
    }}>
      <span style={{
        flex: "none",
        background: "var(--mb-aqua)",
        color: "var(--mb-ink)",
        fontFamily: "var(--font-chrome)",
        fontWeight: 700,
        fontSize: "10.5px",
        padding: "3px 8px",
        margin: "0 0 0 8px",
        border: "1px solid var(--mb-ink)"
      }}>Now open</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Marquee speed={40} gradient={false}>
          <span style={{ color: "var(--mb-magenta)", margin: "0 32px", fontWeight: 700 }}>Now accepting {getCurrentQuarter()} applications</span>
          <span style={{ color: "var(--mb-chalk)", margin: "0 32px" }}>$1,000 micro-grants · no pitch deck required</span>
          <span style={{ color: "var(--mb-butter)", margin: "0 32px", fontWeight: 700 }}>{fundedCount} businesses funded since 2023</span>
          <span style={{ color: "var(--mb-chalk)", margin: "0 32px" }}>{CHAPTER_NAMES}</span>
          <span style={{ color: "var(--mb-aqua)", margin: "0 32px", fontWeight: 700 }}>Join the GNF club — support local founders</span>
        </Marquee>
      </div>
    </div>

    {/* ===== HERO — ink copy panel left, photo right ===== */}
    <section className="mb-block mb-block-magenta" style={{ padding: 0 }}>
      <div className="mb-split" style={{
        "--mb-split-cols": "minmax(0, 1.02fr) minmax(0, 0.98fr)",
        "--mb-split-media-border": "2px solid var(--mb-chalk)"
      }}>
        {/* Left — copy (ink panel) */}
        <div className="mb-split__copy" style={{
          background: "var(--mb-ink)",
          padding: "40px 36px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "18px"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{
              background: "var(--mb-aqua)",
              color: "var(--mb-ink)",
              fontFamily: "var(--font-chrome)",
              fontWeight: 700,
              fontSize: "10.5px",
              padding: "2px 7px",
              border: "1px solid var(--mb-ink)"
            }}>Est. 2023</span>
            <span className="mb-eyebrow" style={{ color: "var(--mb-aqua)", margin: 0 }}>
              Belief capital, not venture capital<span className="mb-cursor" aria-hidden="true" />
            </span>
          </div>

          <h2 className="mb-h1" style={{ color: "var(--mb-chalk)" }}>
            <span className="mb-figure">{HOME_HERO.headline.figure}</span>{HOME_HERO.headline.mid}<em className="mb-key">{HOME_HERO.headline.key}</em>{HOME_HERO.headline.tail}
          </h2>

          <p className="mb-lede" style={{ color: "var(--mb-chalk)", opacity: 0.95, maxWidth: "48ch", margin: 0 }}>
            {HOME_HERO.lede}
          </p>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: "4px" }}>
            <button
              type="button"
              onClick={onPitchClick}
              aria-label="Submit your business pitch for a $1,000 Good Neighbor Fund grant"
              className="win95-btn win95-btn-primary"
            >
              Start an application →
            </button>
          </div>

          {/* Three-figure rail under a hairline rule */}
          <div style={{
            borderTop: "1px solid rgba(255, 255, 255, 0.2)",
            paddingTop: 14,
            marginTop: 8,
            display: "flex",
            gap: 32,
            flexWrap: "wrap"
          }}>
            {[
              { n: fundedCount, label: "businesses funded", color: "var(--mb-butter)" },
              { n: "5", label: "chapters live", color: "var(--mb-aqua)" },
              { n: "0%", label: "equity taken", color: "var(--mb-magenta)" },
            ].map((s) => (
              <div key={s.label}>
                <div style={{ fontFamily: "var(--font-numeral)", fontSize: "var(--tc-data)", color: s.color }}>{s.n}</div>
                <div style={{ fontFamily: "var(--font-chrome)", fontSize: 11, color: "var(--mb-chalk)", opacity: 0.8 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — photo with bevelled caption chip */}
        <div className="mb-split__media" style={{ position: "relative", overflow: "hidden", minHeight: "330px" }}>
          <img
            src="/assets/goodmoonbodywork.webp"
            alt="Good Moon Bodywork founder receiving a $1,000 Good Neighbor Fund micro-grant check"
            width="800"
            height="600"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", inset: 0 }}
          />
          <div style={{
            position: "absolute",
            left: 16,
            bottom: 16,
            display: "flex",
            alignItems: "center",
            gap: 7,
            padding: "5px 10px",
            background: "var(--chrome-face)",
            border: "2px solid",
            borderColor: "var(--bevel-raised)",
            boxShadow: "var(--shadow-raised)",
            fontFamily: "var(--font-chrome)",
            fontSize: 11,
            fontWeight: 600,
            color: "var(--chrome-outline)"
          }}>
            <OSIcon32 kind="trophy" size={16} />
            Good Moon Bodywork · Grant recipient
          </div>
        </div>
      </div>
    </section>

    {/* ===== GROUP-BOX BAND — how it works + impact on chrome ===== */}
    <section className="mb-split" style={{
      "--mb-split-cols": "minmax(0, 1.1fr) minmax(0, 1fr)",
      "--mb-split-gap": "16px",
      background: "var(--chrome-face)",
      borderTop: "var(--border-ink-2)",
      borderBottom: "var(--border-ink-2)",
      padding: "24px 28px"
    }}>
      <fieldset className="win95-groupbox">
        <legend>How it works</legend>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 4 }}>
          {HOW_IT_WORKS_STEPS.map((step) => (
            <div key={step.n} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span style={{
                flex: "none",
                width: 22,
                height: 22,
                background: "var(--mb-magenta)",
                color: "var(--mb-chalk)",
                border: "1px solid var(--chrome-outline)",
                fontFamily: "var(--font-numeral)",
                fontSize: 11,
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>{step.n}</span>
              <div>
                <div style={{ fontFamily: "var(--font-chrome)", fontSize: 13, fontWeight: 700 }}>{step.title}</div>
                <div style={{ fontFamily: "var(--font-chrome)", fontSize: 12, lineHeight: 1.45, color: "var(--chrome-text-soft)" }}>{step.copy}</div>
              </div>
            </div>
          ))}
        </div>
      </fieldset>

      <fieldset className="win95-groupbox">
        <legend>Impact since 2023</legend>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 14px", paddingTop: 4 }}>
          {impactStats.map((s) => (
            <div key={s.label}>
              <div style={{ fontFamily: "var(--font-numeral)", fontSize: "var(--tc-data-lg)", fontWeight: 500, color: "var(--chrome-outline)" }}>{s.n}</div>
              <div style={{ fontFamily: "var(--font-chrome)", fontSize: 11, color: "var(--chrome-text-soft)" }}>{s.label}</div>
            </div>
          ))}
        </div>
        <div style={{
          marginTop: 12,
          background: "var(--mb-ink)",
          color: "var(--mb-chalk)",
          padding: "7px 10px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontFamily: "var(--font-numeral)",
          fontSize: 11
        }}>
          <OSIcon32 kind="chapters" size={18} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{CHAPTER_NAMES}</span>
        </div>
      </fieldset>
    </section>

    {/* ===== TESTIMONIAL + NEXT DEADLINE — paper ===== */}
    <section className="mb-block mb-block-paper" style={{ padding: "36px 32px" }}>
      <div className="mb-split" style={{ "--mb-split-cols": "minmax(0, 1fr) 260px", "--mb-split-gap": "24px", alignItems: "start" }}>
        <div>
          <span className="mb-eyebrow" style={{ color: "var(--mb-magenta)", display: "block", marginBottom: 14 }}>
            {TESTIMONIAL.eyebrow}
          </span>
          <blockquote style={{
            fontFamily: "var(--font-display)",
            fontWeight: 500,
            fontSize: 17,
            lineHeight: 1.45,
            letterSpacing: "-0.01em",
            margin: 0
          }}>
            "{TESTIMONIAL.quote}"
          </blockquote>
          <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 24, height: 2, background: "var(--mb-ink)", display: "inline-block" }} />
            <div>
              <div style={{ fontFamily: "var(--font-chrome)", fontWeight: 700, fontSize: 12, color: "var(--mb-ink)" }}>
                {TESTIMONIAL.name}
              </div>
              <div style={{ fontFamily: "var(--font-numeral)", fontSize: 11, color: "var(--mb-ink-60)" }}>
                {TESTIMONIAL.role}
              </div>
            </div>
          </div>
        </div>

        <div style={{
          background: "var(--chrome-face)",
          border: "2px solid",
          borderColor: "var(--bevel-raised)",
          boxShadow: "var(--shadow-raised)",
          padding: 10
        }}>
          <div style={{
            fontFamily: "var(--font-chrome)",
            fontSize: "var(--tc-label)",
            fontWeight: 700,
            letterSpacing: "var(--track-label)",
            color: "var(--chrome-outline)",
            marginBottom: 7
          }}>Next deadline</div>
          <div style={{
            background: "var(--gnf-bg-dark)",
            color: "var(--counter-text)",
            fontFamily: "var(--font-numeral)",
            fontSize: 15,
            letterSpacing: "0.04em",
            padding: "9px 10px"
          }}>
            {quarterDeadline()}
          </div>
          <button
            type="button"
            className="win95-btn"
            style={{ width: "100%", marginTop: 8, boxSizing: "border-box" }}
            onClick={onPitchClick}
          >
            Start an application
          </button>
        </div>
      </div>
    </section>

    {/* ===== FOOTER — slim ink strip (hit counter, social, legal) ===== */}
    <PageFooter />
  </main>
  );
};

// Shared "Apply Now" CTA + footer rendered at the bottom of every Navigator
// tab page except Resources. Kept local to BrowserWindow since all Navigator
// page components live here.
const PageCTAFooter = ({ onPitchClick }) => (
  <>
    {/* ===== FINAL CTA — Ink ===== */}
    <section className="mb-block mb-block-ink" style={{ textAlign: "center" }}>
      <span className="mb-eyebrow" style={{ color: "var(--mb-magenta)", display: "block", marginBottom: 20 }}>
        {FINAL_CTA.eyebrow}<span className="mb-cursor" aria-hidden="true" />
      </span>

      <h2 className="mb-h1" style={{ color: "var(--mb-chalk)", maxWidth: "24ch", margin: "0 auto 24px" }}>
        {FINAL_CTA.headline.pre}<em className="mb-key" style={{ color: "var(--mb-butter)" }}>{FINAL_CTA.headline.key}</em>
      </h2>

      <p className="mb-lede" style={{ color: "var(--mb-chalk)", opacity: 0.85, maxWidth: "56ch", margin: "0 auto 36px" }}>
        We're looking for passionate founders with bold ideas that create positive impact
        in their communities. No business plan required, just a 60-second pitch video and your authentic vision.
      </p>

      <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
        <button type="button" onClick={onPitchClick} className="mb-btn">
          {FINAL_CTA.cta}
          <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>
        </button>
      </div>
    </section>

    <PageFooter />
  </>
);

// Slim ink footer — hit counter, social links, legal. Shared by every tab.
const PageFooter = () => (
    <footer className="mb-block mb-block-ink" style={{ paddingTop: 24, paddingBottom: 24, textAlign: "center", borderTop: "1px solid rgba(255,255,255,0.15)" }}>
      <HitCounter />

      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 18, marginTop: 18 }}>
        <a
          href={FOOTER_LINKS.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          title="LinkedIn"
          aria-label="LinkedIn"
          style={{ display: "flex", alignItems: "center", color: "var(--mb-chalk)" }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
        </a>
        <a
          href={FOOTER_LINKS.instagram}
          target="_blank"
          rel="noopener noreferrer"
          title="Instagram"
          aria-label="Instagram"
          style={{ display: "flex", alignItems: "center", color: "var(--mb-chalk)" }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
          </svg>
        </a>
        <a
          href={FOOTER_LINKS.newsletter}
          target="_blank"
          rel="noopener noreferrer"
          title="Newsletter"
          aria-label="Newsletter"
          style={{ display: "flex", alignItems: "center", color: "var(--mb-chalk)" }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
          </svg>
        </a>
      </div>

      <div className="mb-numeral" style={{ marginTop: 16, fontSize: 11, color: "rgba(255,255,255,0.7)" }}>
        &copy; {new Date().getFullYear()} {COPYRIGHT_NOTICE} ·{" "}
        <a href={FOOTER_LINKS.terms} target="_blank" rel="noopener noreferrer" className="mb-link-underline" style={{ color: "var(--mb-chalk)" }}>
          TERMS
        </a>
        {" · "}
        <a href={FOOTER_LINKS.privacy} target="_blank" rel="noopener noreferrer" className="mb-link-underline" style={{ color: "var(--mb-chalk)" }}>
          PRIVACY
        </a>
      </div>
    </footer>
);

// -- AwardeesPage
// Navigator-tab awardees: editorial hero band, then the shared GrantAwardees
// component (filters + grid + detail modal). Fetch/filter/sort state is
// owned by GrantAwardees so the same component can be embedded on chapter
// pages without duplicating logic.
const AwardeesPage = ({ onPitchClick }) => (
  <main className="mb-content">
    <section className="mb-block mb-block-butter">
      <div className="mb-split" style={{ "--mb-split-cols": "minmax(0, 1.6fr) minmax(0, 1fr)", "--mb-split-gap": "48px", alignItems: "center" }}>
        <div className="mb-section-head" style={{ margin: 0 }}>
          <span className="mb-eyebrow" style={{ color: "var(--mb-magenta)" }}>{AWARDEES_PAGE.eyebrow}</span>
          <h2 className="mb-h1">{AWARDEES_PAGE.headline}</h2>
          <p className="mb-lede" style={{ maxWidth: "58ch", marginTop: 8 }}>
            Each of these remarkable entrepreneurs received <strong style={{ fontWeight: 600 }}>$1,000 in belief capital</strong> to bring their
            business idea to life. From innovative products to essential services, they're creating
            positive impact and economic opportunity in their neighborhoods.
          </p>
        </div>
        <div style={{
          border: "var(--border-ink-2)",
          boxShadow: "var(--shadow-hard-lg)",
          overflow: "hidden",
          aspectRatio: "1 / 1",
          background: "var(--mb-ink)"
        }}>
          <img
            src="/assets/afterglow.webp"
            alt="Afterglow - Good Neighbor Fund small business, independent bookstore, grant recipient in Buffalo"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        </div>
      </div>
    </section>

    <section className="mb-block mb-block-paper" style={{ paddingTop: 32, paddingBottom: 48 }}>
      <GrantAwardees />
    </section>

    <PageCTAFooter onPitchClick={onPitchClick} />
  </main>
);

// -- ChaptersPage --
// Renders the list dynamically from the /chapters Firestore collection.
// Falls back to LEGACY_CHAPTER_CARDS if the collection is empty or the load fails.
const ChaptersPage = ({ setPage, onLpApplicationClick, onPitchClick }) => {
  const [dynamicChapters, setDynamicChapters] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'chapters'));
        const list = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(c => c.active !== false)
          .sort((a, b) => {
            const ao = typeof a.order === 'number' ? a.order : 999;
            const bo = typeof b.order === 'number' ? b.order : 999;
            if (ao !== bo) return ao - bo;
            return (a.name || '').localeCompare(b.name || '');
          });
        if (!cancelled) {
          setDynamicChapters(list);
          setLoaded(true);
        }
      } catch (e) {
        console.error('ChaptersPage: failed to load /chapters, using fallback', e);
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const chapterCards = (loaded && dynamicChapters.length > 0)
    ? dynamicChapters.map(c => ({
        name: c.name,
        foundedYear: c.foundedYear,
        tagline: c.tagline,
        pageSlug: c.pageSlug || c.id,
      }))
    : LEGACY_CHAPTER_CARDS;

  return (
  <main className="mb-content">
    {/* ===== HERO — Grape ===== */}
    <section className="mb-block mb-block-grape" style={{ padding: 0 }}>
      <div className="mb-split" style={{
        "--mb-split-cols": "minmax(0, 1.1fr) minmax(0, 1fr)",
        "--mb-split-media-border": "var(--border-chalk-2)",
        minHeight: "460px"
      }}>
        <div style={{
          padding: "var(--s-block-pad-y) var(--s-block-pad-x)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "var(--s-stack)"
        }}>
          <span className="mb-eyebrow" style={{ color: "var(--mb-butter)" }}>{CHAPTERS_HERO.eyebrow}</span>
          <h2 className="mb-h1" style={{ color: "var(--mb-chalk)" }}>
            {CHAPTERS_HERO.headline.pre}<em className="mb-key" style={{ color: "var(--mb-aqua)" }}>{CHAPTERS_HERO.headline.key}</em>
          </h2>
          <p className="mb-lede" style={{ color: "var(--mb-chalk)", opacity: 0.9, marginTop: 8, maxWidth: "48ch" }}>
            {CHAPTERS_HERO.lede}
          </p>
        </div>
        <div className="mb-split__media" style={{ position: "relative", overflow: "hidden", minHeight: "360px" }}>
          <img
            src="/assets/chapters-hero.webp"
            alt="Good Neighbor Fund chapter community gathering"
            width="800"
            height="600"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", inset: 0 }}
          />
        </div>
      </div>
    </section>

    {/* ===== CHAPTER CARDS — Paper ===== */}
    <section className="mb-block mb-block-paper">
      <div className="mb-grid mb-grid-2" style={{ gap: 28 }}>
        {chapterCards.map(c => (
          <article key={c.name} className="mb-card" style={{ padding: 28, display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
              <h3 className="mb-h3" style={{ margin: 0 }}>{c.name}</h3>
              {c.foundedYear && (
                <span className="mb-numeral" style={{ fontSize: "var(--tc-data-sm)", color: "var(--mb-ink-60)", whiteSpace: "nowrap" }}>
                  Est. {c.foundedYear}
                </span>
              )}
            </div>

            {c.tagline && (
              <p className="mb-body" style={{ margin: 0, maxWidth: "48ch" }}>{c.tagline}</p>
            )}

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6 }}>
              <button
                type="button"
                onClick={() => window.open(`/${c.pageSlug}`, '_blank')}
                className="mb-btn mb-btn-ink"
                style={{ padding: "10px 16px", fontSize: 12 }}
              >
                {CHAPTER_CARD_CTAS.visit}
                <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>
              </button>
              <button
                type="button"
                onClick={() => onLpApplicationClick && onLpApplicationClick(c.name)}
                className="mb-btn mb-btn-chalk"
                style={{ padding: "10px 16px", fontSize: 12 }}
              >
                {CHAPTER_CARD_CTAS.join}
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>

    {/* ===== HOW IT WORKS — Aqua ===== */}
    <section className="mb-block mb-block-aqua" style={{ padding: 0 }}>
      <div className="mb-split" style={{ minHeight: "420px" }}>
        <div style={{
          padding: "var(--s-block-pad-y) var(--s-block-pad-x)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "var(--s-stack)"
        }}>
          <div className="mb-section-head" style={{ margin: 0, maxWidth: "56ch" }}>
            <span className="mb-eyebrow" style={{ color: "var(--mb-ink)" }}>{HOW_CHAPTERS_WORK.eyebrow}</span>
            <h2 className="mb-h2" style={{ maxWidth: "22ch" }}>
              {HOW_CHAPTERS_WORK.headline}
            </h2>
          </div>
          <p className="mb-lede" style={{ maxWidth: "64ch" }}>
            {HOW_CHAPTERS_WORK.lede}
          </p>
        </div>
        <div className="mb-split__media" style={{ position: "relative", overflow: "hidden", minHeight: "360px" }}>
          <img
            src={HOW_CHAPTERS_WORK.image.src}
            alt={HOW_CHAPTERS_WORK.image.alt}
            width="800"
            height="600"
            loading="lazy"
            decoding="async"
            style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", inset: 0 }}
          />
        </div>
      </div>
    </section>

    {/* ===== COLLECTIVE GIVING — Paper ===== */}
    <section className="mb-block mb-block-paper">
      <div className="mb-split" style={{ "--mb-split-cols": "minmax(0, 1fr) minmax(0, 1.4fr)", "--mb-split-gap": "64px", alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <div className="mb-section-head" style={{ margin: 0 }}>
            <span className="mb-eyebrow" style={{ color: "var(--mb-magenta)" }}>{COLLECTIVE_GIVING.eyebrow}</span>
            <h2 className="mb-h2">{COLLECTIVE_GIVING.headline}</h2>
          </div>
          <img
            src={COLLECTIVE_GIVING.image.src}
            alt={COLLECTIVE_GIVING.image.alt}
            width="800"
            height="600"
            loading="lazy"
            decoding="async"
            style={{ width: "100%", height: "auto", display: "block", border: "var(--border-ink-2)" }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--s-stack)" }}>
          <p className="mb-lede" style={{ maxWidth: "58ch" }}>
            {COLLECTIVE_GIVING.lede}
          </p>
          <p className="mb-body" style={{ maxWidth: "60ch" }}>
            {COLLECTIVE_GIVING.body1}
          </p>
          <p className="mb-body" style={{ maxWidth: "60ch" }}>
            {COLLECTIVE_GIVING.body2.pre}<strong style={{ fontWeight: 600 }}>{COLLECTIVE_GIVING.body2.strong}</strong>{COLLECTIVE_GIVING.body2.post}
          </p>
          <p className="mb-body" style={{ maxWidth: "60ch" }}>
            Each quarter, LPs come together to review applications, share dinner, and vote on who receives
            new micro-grants. The process is human, and deeply rooted in community. We follow up with founders
            over coffee, hand off giant checks, and help make their first steps a little more possible.
          </p>
        </div>
      </div>
    </section>

    {/* ===== START A CHAPTER — Magenta ===== */}
    <section className="mb-block mb-block-magenta" style={{ textAlign: "center" }}>
      <span className="mb-eyebrow" style={{ color: "var(--mb-butter)", display: "block", marginBottom: 20 }}>
        {START_A_CHAPTER.eyebrow}
      </span>
      <h2 className="mb-h1" style={{ color: "var(--mb-chalk)", maxWidth: "20ch", margin: "0 auto 24px" }}>
        {START_A_CHAPTER.headline.pre}<em className="mb-key" style={{ color: "var(--mb-butter)" }}>{START_A_CHAPTER.headline.key}</em>
      </h2>
      <p className="mb-lede" style={{ color: "var(--mb-chalk)", opacity: 0.9, maxWidth: "56ch", margin: "0 auto 36px" }}>
        {START_A_CHAPTER.lede}
      </p>
      <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => window.location.href = START_A_CHAPTER.primaryHref}
          className="mb-btn mb-btn-butter"
        >
          {START_A_CHAPTER.primaryCta}
          <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>
        </button>
        <button
          type="button"
          onClick={() => window.open(START_A_CHAPTER.secondaryHref, "_blank")}
          className="mb-btn mb-btn-chalk"
        >
          {START_A_CHAPTER.secondaryCta}
        </button>
      </div>
    </section>

    <PageCTAFooter onPitchClick={onPitchClick} />
  </main>
  );
};



// -- DonatePage --
// The donate sections live in DonateContent, shared with the standalone
// shareable /donate page (src/pages/standalone/DonatePage.jsx).
const DonatePage = ({ onPitchClick }) => (
  <main className="mb-content">
    <DonateContent />
    <PageCTAFooter onPitchClick={onPitchClick} />
  </main>
);

